import { DateTime } from "luxon";

import {
  ExperienceStatus,
  GenericExperienceFe,
  PutExperienceScheduleRequestBodyType,
  ScheduleAction,
  ScheduleDto,
} from "@intelligems/ig-types";
import {
  ScheduleActionType,
  ScheduleErrors,
} from "@src/features/scheduleExperience/types";

export const getStartDisabledReason = (experience: GenericExperienceFe) => {
  if (experience.status === "started") {
    return "testStarted";
  }
  return undefined;
};

export const getPausedDisabledReason = (
  experience: GenericExperienceFe,
  startAtSchedule: ScheduleDto | null,
  endAtSchedule: ScheduleDto | null,
  currStartDateTime: DateTime | null,
  currEndDateTime: DateTime | null
) => {
  if (experience.status === "paused" && experience.category === "experiment") {
    return "testPaused";
  }
  if (
    experience.category === "experiment" &&
    experience.testTypes.hasTestPricing
  ) {
    return "priceTest";
  }
  if (
    experience.status !== "started" &&
    !startAtSchedule?.executionTime &&
    !currStartDateTime
  ) {
    return "startMissing";
  }
  if ((endAtSchedule?.executionTime && currEndDateTime) || currEndDateTime) {
    return "endSet";
  }
  return undefined;
};

export const getEndedDisabledReason = (
  experience: GenericExperienceFe,
  startAtSchedule: ScheduleDto | null,
  pauseAtSchedule: ScheduleDto | null,
  currStartDateTime: DateTime | null,
  currPauseDateTime: DateTime | null
) => {
  if (experience.status === "ended") {
    return "testEnded";
  }
  if (
    experience.category === "experiment" &&
    experience.testTypes.hasTestPricing
  ) {
    return "priceTest";
  }
  if (
    experience.status !== "started" &&
    !startAtSchedule?.executionTime &&
    !currStartDateTime
  ) {
    return "startMissing";
  }
  if (
    (pauseAtSchedule?.executionTime && currPauseDateTime) ||
    currPauseDateTime
  ) {
    return "pauseSet";
  }
  return undefined;
};

export const isTextfieldOpen = (
  action: ScheduleAction,
  experience: GenericExperienceFe,
  pauseAtSchedule: ScheduleDto | null,
  endAtSchedule: ScheduleDto | null
) => {
  if (action === "start" || experience.status === "started") {
    return true;
  }

  // if (action === "end" && experience.status === "paused") {
  //   return true;
  // }
  if (action === "pause" && pauseAtSchedule) {
    return true;
  }
  if (action === "end" && endAtSchedule) {
    return true;
  }
  return false;
};

export const isConfirmDisabled = (
  errors: ScheduleErrors,
  experience: GenericExperienceFe,
  startDateTime: DateTime | null
) => {
  if (Object.values(errors).filter((error) => error).length > 0) {
    return {
      isDisabled: true,
      reason: "Select a stop time that is after the start time",
    };
  }
  if (experience.status !== "started" && !startDateTime) {
    return {
      isDisabled: true,
      reason: "Select a start time",
    };
  }
  return { isDisabled: false, reason: "" };
};

export const buildSavePayload = ({
  experienceId,
  startAtSchedule,
  startDateTime,
  pauseAtSchedule,
  pauseDateTime,
  endDateTime,
  endAtSchedule,
  experienceStatus,
  cancelSchedule,
  updatePricesToHighestVariation,
}: {
  experienceId: string;
  cancelSchedule: boolean;
  startAtSchedule: ScheduleDto | null;
  startDateTime: DateTime | null;
  pauseAtSchedule: ScheduleDto | null;
  pauseDateTime: DateTime | null;
  endAtSchedule: ScheduleDto | null;
  endDateTime: DateTime | null;
  experienceStatus: ExperienceStatus;
  updatePricesToHighestVariation: boolean;
}): PutExperienceScheduleRequestBodyType => {
  const payload: PutExperienceScheduleRequestBodyType = [];

  const addToPayload = (
    action: ScheduleActionType,
    dateTime: DateTime | null,
    currSchedule: ScheduleDto | null
  ) => {
    if (cancelSchedule) {
      if (currSchedule) {
        payload.push({
          experienceId,
          action,
          executionTime: 0,
          isDelete: true,
        });
      }
    } else {
      if (
        (dateTime && !currSchedule) ||
        (currSchedule &&
          dateTime &&
          currSchedule.executionTime !== dateTime.toMillis())
      ) {
        payload.push({
          experienceId,
          action,
          executionTime: dateTime.toMillis(),
          isDelete: false,
          updatePricesToVariation: updatePricesToHighestVariation,
          variationId: updatePricesToHighestVariation ? null : undefined,
        });
      } else if (currSchedule && !dateTime) {
        payload.push({
          experienceId,
          action,
          executionTime: 0,
          isDelete: true,
        });
      }
    }
  };
  if (experienceStatus !== "started") {
    addToPayload("start", startDateTime, startAtSchedule);
  }
  addToPayload("pause", pauseDateTime, pauseAtSchedule);
  addToPayload("end", endDateTime, endAtSchedule);

  return payload;
};
